<script setup lang="ts">
import { computed, ref } from 'vue';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue';
import type { SizeProps } from './type';
import type { CustomSizeProps } from './type';
import SizeFormEdit from './SizeFormEdit.vue';

type Props = {
  mobileOnly?: boolean;
  isCustom?: CustomSizeProps;
  value?: Record<string, SizeProps>;
  currentType?: 'small' | 'medium' | 'large';
  currentScreen?: string;
  devices?: string[];
  component?: 'tag' | string;
  componentTag?: string;
};

const props = defineProps<Props>();
const resetKey = ref(0);

const initValue =
  props.componentTag === 'Icon'
    ? {
        small: {
          vertical: '8px',
          horizontal: '8px',
        },
        medium: {
          vertical: '16px',
          horizontal: '16px',
        },
        large: {
          vertical: '24px',
          horizontal: '24px',
        },
      }
    : props.component === 'tag'
    ? {
        small: {
          vertical: '2px',
          horizontal: '8px',
        },
        medium: {
          vertical: '12px',
          horizontal: '4px',
        },
        large: {
          vertical: '16px',
          horizontal: '8px',
        },
      }
    : {
        small: {
          vertical: '4px',
          horizontal: '16px',
        },
        medium: {
          vertical: '8px',
          horizontal: '24px',
        },
        large: {
          vertical: '12px',
          horizontal: '32px',
        },
      };

const defaultValue = ref(initValue);
const emit = defineEmits<{
  (e: 'change', device: string, value?: SizeProps): void;
  (e: 'onChange', device: string, value?: SizeProps): void;
  (e: 'changeScreen', screenId: string): void;
  (e: 'close'): void;
  (e: 'reset'): void;
}>();

const extendValue = computed(() => {
  return Object.fromEntries(
    props.devices?.map((device, index) => {
      const composeDeviceValue = props.devices?.slice(0, index + 1).reduce((curr, deviceId) => {
        const controlDevice = props.value?.[deviceId];
        return {
          ...curr,
          ...controlDevice,
        };
      }, {} as SizeProps);
      return [device, composeDeviceValue];
    }) ?? [],
  );
});

const selectedTab = computed(() => {
  return props.currentScreen
    ? props.devices?.includes(props.currentScreen)
      ? props.devices?.indexOf(props.currentScreen)
      : 0
    : 0;
});

const changeTab = (index: number) => {
  const screenId = props.devices?.[index];
  if (screenId) {
    emit('changeScreen', screenId);
  }
};

const onChange = (device: string, controlId: string, value?: any) => {
  const extendV = extendValue.value[device];
  if (extendV) {
    const newV: SizeProps = {
      ...defaultValue.value[props.currentType ?? 'medium'],
      ...(Object.keys(extendV).length !== 0 && extendValue.value[device]),
      [controlId]: value,
    };

    emit('change', device, newV);
  }
};

const onChangeData = (device: string, controlId: string, value?: any) => {
  const extendV = extendValue.value[device];
  if (extendV) {
    const newV: SizeProps = {
      ...defaultValue.value[props.currentType ?? 'medium'],
      ...(Object.keys(extendV).length !== 0 && extendValue.value[device]),
      [controlId]: value,
    };
    emit('onChange', device, newV);
  }
};

// const close = () => {
//   emit('close');
// };
</script>

<template>
  <g-popover
    :closeable="true"
    class="aspect-square h-full"
    :overlay="false"
    arrow-class="!text-dark-400 translate-x-[-42px]"
    overlay-container="#root-modal"
    placement="bottom-end"
    wrapper-class="translate-x-[42px]"
    cls="!p-0 mt-2">
    <template #default="{ open }">
      <!-- <button
        ref="btn"
        class="rounded-medium bg-primary-300 text-dark-low flex aspect-square h-36 shrink-0 items-center justify-center transition-colors duration-200 hover:bg-[#6385FF]">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4.63388 9.11612C5.12204 9.60427 5.12204 10.3957 4.63388 10.8839C4.14573 11.372 3.35427 11.372 2.86612 10.8839C2.37796 10.3957 2.37796 9.60427 2.86612 9.11612C3.35427 8.62796 4.14573 8.62796 4.63388 9.11612Z"
            fill="white" />
          <path
            d="M17.1339 9.11612C17.622 9.60427 17.622 10.3957 17.1339 10.8839C16.6457 11.372 15.8543 11.372 15.3661 10.8839C14.878 10.3957 14.878 9.60427 15.3661 9.11612C15.8543 8.62796 16.6457 8.62796 17.1339 9.11612Z"
            fill="white" />
          <path
            d="M10.8839 10.8839C11.372 10.3957 11.372 9.60427 10.8839 9.11612C10.3957 8.62796 9.60427 8.62796 9.11612 9.11612C8.62796 9.60427 8.62796 10.3957 9.11612 10.8839C9.60427 11.372 10.3957 11.372 10.8839 10.8839Z"
            fill="white" />
        </svg>
      </button> -->
      <g-button :active="open" type="moreSetting" size="medium" :only-icon="true" class="h-full">
        <g-base-icon name="more-setting-20" width="20" height="20" view-box="0 0 20 20"></g-base-icon>
      </g-button>
    </template>
    <template #content="{ close }">
      <div ref="popup" class="bg-dark-400 rounded-medium w-screen max-w-[264px] transform px-4 sm:px-0">
        <div class="flex items-center justify-between p-16">
          <h3 class="text-14 text-dark-high font-semibold">Custom inside space</h3>
          <div
            class="hover:bg-light-100 rounded-medium flex aspect-square w-24 cursor-pointer items-center justify-center hover:bg-opacity-20"
            @click="close">
            <g-base-icon name="close" width="16" height="16" class="text-dark-high"></g-base-icon>
          </div>
        </div>
        <div v-if="mobileOnly" class="px-12 py-8">
          <SizeFormEdit
            :reset-key="resetKey"
            :vertical="
              isCustom !== undefined
                ? extendValue['desktop']?.vertical
                : defaultValue[currentType ?? 'medium']?.vertical
            "
            :horizontal="
              isCustom !== undefined
                ? extendValue['desktop']?.horizontal
                : defaultValue[currentType ?? 'medium']?.horizontal
            "
            @change="(...args) => onChange('desktop', ...args)"
            @on-change="(...args) => onChangeData('desktop', ...args)" />
        </div>
        <TabGroup v-else :selected-index="selectedTab" @change="changeTab">
          <TabList class="rounded-small relative mx-16 flex items-center gap-16">
            <Tab v-for="device in devices" :key="device" v-slot="{ selected }" as="template">
              <button
                :class="{
                  '!border-primary-275 text-dark-high': device === currentScreen,
                  'text-dark-low': !selected,
                  'text-dark-disabled': device !== currentScreen,
                }"
                class="text-12 z-5 border-b-2 border-transparent py-8 text-center capitalize focus-visible:outline-none">
                {{ device }}
              </button>
            </Tab>
            <div class="border-light-500 absolute bottom-0 left-0 z-0 w-full border-b border-opacity-20"></div>
          </TabList>
          <TabPanels class="p-16">
            <TabPanel v-for="d in devices" :key="d">
              <SizeFormEdit
                :reset-key="resetKey"
                :vertical="
                  isCustom !== undefined
                    ? extendValue[d]?.vertical
                      ? extendValue[d]?.vertical
                      : defaultValue[currentType ?? 'medium']?.vertical
                    : defaultValue[currentType ?? 'medium']?.vertical
                "
                :horizontal="
                  isCustom !== undefined
                    ? extendValue[d]?.horizontal
                      ? extendValue[d]?.horizontal
                      : defaultValue[currentType ?? 'medium']?.horizontal
                    : defaultValue[currentType ?? 'medium']?.horizontal
                "
                @change="(...args) => onChange(d, ...args)"
                @on-change="(...args) => onChangeData(d, ...args)" />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>
    </template>
  </g-popover>
</template>
